import { Actor } from "../../models/Actor";
import { Prop } from "../../models/Prop";
import Take3D from "../../models/Take3D";
import {
  describeTakeStartMismatch,
  getMovedHardAttachments,
  getPlacedHeldPropIds,
  getTakeStartMismatches,
} from "../../helpers/TakeEndState";

/**
 * Warnings for a take whose start state is off: it doesn't start the way the
 * take before it ends, or a prop is both placed and held.
 */
export function getTakeStartMismatchMessages(
  takes: Take3D[],
  takeId: string,
  propPresets: Prop[],
  actors: Actor[],
): string[] {
  const index = takes.findIndex((take) => take.id === takeId);
  if (index < 0) return [];

  const take = takes[index];
  const previousTake = index > 0 ? takes[index - 1] : undefined;
  const getPropName = (propId: string) =>
    propPresets.find((prop) => prop.id === propId)?.name ?? "A prop";
  const getActorName = (actorId: string) =>
    actors.find((actor) => actor.id === actorId)?.workingName ?? "an actor";

  return [
    ...getTakeStartMismatches(previousTake, take).map((mismatch) =>
      describeTakeStartMismatch(
        mismatch,
        previousTake?.name || "The previous take",
        getPropName,
        getActorName,
      ),
    ),
    ...getPlacedHeldPropIds(take).map(
      (propId) =>
        `${getPropName(propId)} is placed on a position but also held by ${getActorName(take.attachments[propId].id)}; the take starts with it in the hand.`,
    ),
    ...getMovedHardAttachments(previousTake, take).map(
      ({ propId, actorId }) =>
        `${getActorName(actorId)} moves to another position but is still attached to ${getPropName(propId)}. Fix take start removes the attachment, so ${getActorName(actorId)} gets off before moving.`,
    ),
  ];
}
